/**
 * merchant-widget.js — <merchant-widget>, Google's store widget, loaded only after marketing consent.
 *
 * Why it exists: the Google & YouTube app's own "Store widget" embed loads the widget on every
 * page before the visitor answers the banner, and its frame sets a google.com cookie (NID) even
 * after "Deny" (measured 2026-10-01). The embed's markup cannot be changed, so the theme loads the
 * same widget itself, and only once Cookiebot reports marketing consent. The app embed must stay
 * off, or the widget loads twice and the leak returns.
 *
 * Cookiebot fires `CookiebotOnAccept` both when the visitor accepts and on a later page load where
 * consent is already stored; `Cookiebot.consent.marketing` covers the case where Cookiebot ran
 * before this upgraded. Without JS, or without consent, nothing loads.
 */
(() => {
  if (customElements.get('merchant-widget')) return;

  const SRC = 'https://www.gstatic.com/shopping/merchant/merchantwidget.js';

  class MerchantWidget extends HTMLElement {
    connectedCallback() {
      this.onAccept = () => this.maybeLoad();
      window.addEventListener('CookiebotOnAccept', this.onAccept);
      this.maybeLoad();
    }

    disconnectedCallback() {
      window.removeEventListener('CookiebotOnAccept', this.onAccept);
    }

    maybeLoad() {
      const consent = window.Cookiebot && window.Cookiebot.consent;
      if (!consent || !consent.marketing || this.loaded) return;
      this.loaded = true;
      const script = document.createElement('script');
      script.src = SRC;
      script.addEventListener('load', () => {
        window.merchantwidget.start({
          position: 'LEFT_BOTTOM',
          sideMargin: 36,
          bottomMargin: 36,
          mobileSideMargin: 16,
          mobileBottomMargin: 46,
          merchant_id: this.dataset.merchantId,
        });
      });
      document.head.appendChild(script);
    }
  }

  customElements.define('merchant-widget', MerchantWidget);
})();
